import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import config from '../Config';

function getUserPool() {
  return new CognitoUserPool({
    UserPoolId: config.USER_POOL,
    ClientId: config.COGNITO_CLIENT_ID,
  });
}

function makeCognitoUser(email) {
  return new CognitoUser({ Username: email, Pool: getUserPool() });
}

/** Sign in and return { accessToken, idToken, refreshToken } */
export function cognitoLogin(email, password) {
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });
    const user = makeCognitoUser(email);
    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        resolve({
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        });
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: () => reject(new Error('New password required. Please reset your password.')),
    });
  });
}

/** Sign up with email + password. */
/**
 * Register a new user directly with Cognito.
 *
 * turnstileToken travels as clientMetadata, which is where the PreSignUp Lambda
 * trigger reads it from. Signup is browser-to-Cognito, so that trigger is the only
 * place the challenge can be enforced before the account exists.
 */
export function cognitoSignup(email, password, turnstileToken = '') {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
    ];
    const clientMetadata = turnstileToken ? { turnstileToken } : undefined;
    pool.signUp(email, password, attributes, null, (err, result) => {
      if (err) return reject(err);
      resolve(result);
    }, clientMetadata);
  });
}

/** Confirm signup with the 6-digit verification code Cognito sends to the email. */
export function cognitoConfirmSignup(email, code) {
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    user.confirmRegistration(code, true, (err, result) => {
      if (err) return reject(err);
      resolve(result);
    });
  });
}

/** Resend the confirmation code for signup. */
export function cognitoResendCode(email) {
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    user.resendConfirmationCode((err, result) => {
      if (err) return reject(err);
      resolve(result);
    });
  });
}

/** Initiate forgot-password flow (sends code to email). */
export function cognitoForgotPassword(email) {
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    user.forgotPassword({
      onSuccess: (data) => resolve(data),
      onFailure: (err) => reject(err),
    });
  });
}

/** Confirm new password with the code from forgotPassword. */
export function cognitoConfirmPassword(email, code, newPassword) {
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** Sign out the current user (local sign-out). */
export function cognitoLogout() {
  const pool = getUserPool();
  const user = pool.getCurrentUser();
  if (user) user.signOut();
}

